import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import * as CMxRuntime from './runtime';
import { Duration, intervalToDuration } from 'date-fns';

dayjs.extend(advancedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);

// -------------------------------------
// Types
// -------------------------------------

export enum DateManipulateOperation {
  ADD = 'ADD',
  SUBTRACT = 'SUBTRACT'
}

export enum DateManipulateUnit {
  DAY = 'd',
  MONTH = 'M',
  YEAR = 'y'
}

type DateDelta = [number, DateManipulateUnit];

type ParseOptions = {
  throw: boolean;
  localized: boolean;
  offset: {
    action: keyof typeof DateManipulateOperation;
    delta: DateDelta;
  };
};

// -------------------------------------
// Constants
// -------------------------------------

const DEFAULT_PARSE_OPTIONS: ParseOptions = {
  throw: false,
  localized: true,
  offset: undefined
};

const DATE_TIME_REGEX = /^(.*)\[(.*)\]/;

// -------------------------------------
// Functions
// -------------------------------------

const _formateCMXDT = (cmxDateTime: CMxCommonApp.DateTime, format: string) => {
  return cmxDateTime?.dayjs?.format(format) ?? cmxDateTime?.raw;
};

// -------------------------------------
// Public API
// -------------------------------------

export const calculateDuration = (startDate: Date | string, endDate: Date | string) => {
  // to validate date string and handle negative case
  const startDateStr = ( startDate instanceof Date ? startDate.toISOString() : String(startDate || '')).match(DATE_TIME_REGEX);
  const endDateStr =  (endDate instanceof Date ? endDate.toISOString() : String(endDate || '')).match(DATE_TIME_REGEX);
  if (!startDateStr || !endDateStr) return dayjs.duration(0);
  // calculate duration for valid startDate and enddate
  const startTime = dayjs(reformatDate(startDateStr[1]));
  const endTime = dayjs(reformatDate(endDateStr[1]));
  return dayjs.duration(endTime.diff(startTime));
};

export const MONTH_ABBR = Object.freeze({
  '01': 'Jan',
  '02': 'Feb',
  '03': 'Mar',
  '04': 'Apr',
  '05': 'May',
  '06': 'Jun',
  '07': 'Jul',
  '08': 'Aug',
  '09': 'Sep',
  '10': 'Oct',
  '11': 'Nov',
  '12': 'Dec'
});

export const FORMATS = {
  DATE: 'YYYY-MM-DD',
  DATE_OF_SERVICE: 'M/D/YYYY',
  BIRTH_DATE: 'M-D-YYYY',
  LONG_DATE: 'MMMM Do, YYYY',
  TIME: 'h:mm:ss a',
  TIME_WITHOUT_SECONDS: 'h:mm a',
  TIME_WITH_ZONE: 'h:mm:ss a z',
  DATE_TIME: 'YYYY-MM-DD h:mm:ss a',
  DATE_TIME_WITH_ZONE: 'YYYY-MM-DD h:mm:ss a z',
  DATE_TIME_EULA: 'YYYY-MM-DDTHH:mm:ss',
  LONG_DATE_TIME: 'MMM Do YYYY, h:mm:ss a',
  DATE_TIME_UI: 'M/D/YYYY h:mm a',
  DATE_TIME_WITHOUT_TIMEZONE: 'YYYY-MM-DD HH:mm:ss'
};

export const parse = (
  datetime: string,
  options: ParseOptions = DEFAULT_PARSE_OPTIONS
): CMxCommonApp.DateTime => {
  const cmxDateTime: CMxCommonApp.DateTime = {
    isoString: '',
    raw: datetime,
    runtimeTimezone: undefined,
    successfullyParsed: false,
    dayjs: null
  };
  const matched = `${datetime}`.match(DATE_TIME_REGEX);

  try {
    const input = matched && matched.length ? matched[1] : cmxDateTime.raw;
    const day: dayjs.Dayjs = dayjs(input);

    if (!day.isValid()) {
      throw new Error('invalid input date format');
    }

    let matchedTz = matched && matched.length ? matched[2] : false;

    if (!options.localized && !matchedTz && options.throw) {
      throw new Error(
        'require un-localized timezone, but unable to determine it'
      );
    }

        const runtimeTimezone = options.localized
      ? CMxRuntime.getResolvedDateTimeFormatOptions().timeZone
      : matchedTz;

    if (!runtimeTimezone && options.throw) {
      throw new Error('no runtime timezone available');
    }

    if (runtimeTimezone) {
      if (options.localized) {
        cmxDateTime.dayjs = dayjs.utc(day).tz(runtimeTimezone);
      } else {
        // Format in the string's timezone so output is deterministic and not tied to system TZ
        cmxDateTime.dayjs = dayjs.utc(day).tz(runtimeTimezone);
        cmxDateTime.isoString = day.toISOString();
      }
      cmxDateTime.runtimeTimezone = runtimeTimezone;
    } else {
      cmxDateTime.isoString = day.toISOString();
    }

    cmxDateTime.successfullyParsed = true;
  } catch (e) {
    cmxDateTime.successfullyParsed = false;
  }

  return cmxDateTime;
};

const reformatDate = (datetime: string): string => {
  // reformats dates like 2023-04-13 01:57:55 EDT => 13 Apr 2023 01:57:55 EDT
  const date =
    datetime.substring(8, 10) +
    ' ' +
    MONTH_ABBR[datetime.substring(5, 7)] +
    ' ' +
    datetime.substring(0, 4) +
    ' ' +
    datetime.substring(11);

  return date;
};

export const format = (
  candidate: string | Date,
  format: string = FORMATS.DATE_TIME,
  options: ParseOptions = DEFAULT_PARSE_OPTIONS
) => {
  if (candidate === null || candidate === undefined) {
    return '';
  }

  if (candidate instanceof Date) {
    let dayjsDate = dayjs(candidate);
    if (options.offset) {
      const [value, unit] = options.offset.delta;
      if (options.offset.action === DateManipulateOperation.ADD) {
        candidate = dayjsDate.add(value, unit).toISOString();
      } else if (options.offset.action === DateManipulateOperation.SUBTRACT) {
        candidate = dayjsDate.subtract(value, unit).toISOString();
      }
    } else {
      candidate = dayjsDate.toISOString();
    }
  }

  let cmxDateTime: CMxCommonApp.DateTime;
  if (candidate && typeof candidate === 'string') {
    cmxDateTime = parse(candidate, options);
  }

  if (!cmxDateTime?.successfullyParsed && typeof candidate === 'string') {
    // if date did not parse, return it as is
    return candidate;
  }

  return _formateCMXDT(cmxDateTime, format);
};

export const sortByDate = (
  dateA: string | undefined = '',
  dateB: string | undefined = ''
): number => {
  dateA = dateA ?? '';
  dateB = dateB ?? '';

  if (dateA > dateB) {
    return -1;
  }
  if (dateA < dateB) {
    return 1;
  }

  return 0;
};

/**
 * Method to return time zone short code for a given full time zone ex: "America/New_York" and a date str
 * @param timeZone
 * @returns
 */
export const getTZShortCode = (timeZone, dateStr) => {
  const isIntlPresent =
    typeof Intl === 'object' &&
    typeof Intl.DateTimeFormat === 'function' &&
    typeof Intl.DateTimeFormat().formatToParts === 'function';

  if (timeZone && dateStr && isIntlPresent) {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName: 'short'
      });
      const date = new Date(dateStr);
      const tzCode = formatter
        .formatToParts(date)
        .find(part => part.type === 'timeZoneName');
      return tzCode?.value ?? '';
    } catch (e) {
      return null;
    }
  }
  return isIntlPresent ? null : '';
};

/**
 * Method to check if timezone is present for dates like "2023-11-03 18:00:00" or "2023-11-03 18:00:00 EST"
 * @param dateTimeStr
 * @returns
 */
export const isTimezonePresent = dateTimeStr => {
  // Regular expression to match typical timezone abbreviations (3-4 uppercase letters)
  const regex = /[A-Z]{3,4}$/;
  return regex.test(dateTimeStr);
};

/**
 * Method that removes the square brackets for match javascript tz standard "2023-11-03 18:00:00 [EST]" to "2023-11-03 18:00:00 EST"
 * @param inputDate
 * @returns {string}
 */
export const clearSquareBraces = (inputDate: string) => {
  if (!inputDate) {
    return inputDate;
  }

  // Split the input string into date and timezone parts
  const [datePart, timePart, timezonePart] = inputDate.split(' ');

  // Remove square brackets from the timezonePart
  const cleanedTimezone = timezonePart
    ? timezonePart?.replace(/[\[\]]/g, '')
    : '';

  // Combine the date and cleaned timezone parts
  const outputDate = `${datePart} ${timePart} ${cleanedTimezone}`;

  return outputDate;
};

/**
 * Method to check if the date has existing timezone,
 * If the date string already contains timezone information,
 * it replaces the existing timezone with [timezone].
 * If the date string does not have timezone information,
 * it appends [timezone] short code for the provided timezone.
 * @param timeZoneStr
 * @param dateStr
 * @returns
 */
export const setTimeZoneForDate = (
  timeZoneStr: CMxAPI.NullableString | undefined,
  dateStr: CMxAPI.NullableString | undefined
) => {
  const timezonePresent = isTimezonePresent(dateStr);
  const shortCodeTZ = getTZShortCode(timeZoneStr, dateStr);
  if (!shortCodeTZ) {
    return dateStr;
  }
  if (dateStr) {
    if (timezonePresent) {
      return dateStr?.replace(shortCodeTZ, `[${shortCodeTZ}]`);
    } else {
      return `${dateStr} [${shortCodeTZ}]`;
    }
  }
  return dateStr;
};

export const wrapTimezoneInBracket = dateTimeStr => {
  // Return early if null or undefined to preserve the original value
  if (dateTimeStr === null || dateTimeStr === undefined) {
    return '';
  }
  // Regular expression to match typical timezone abbreviations (3-4 uppercase letters)
  const regex = /([A-Z]{3,4})$/;
  return dateTimeStr?.replace(regex, '[$1]');
};

//Converts a date to a string in the format YYYY-MM-DD. Usefull for certain api calls
export const convertToDateFormat = (_date: Date) => {
  const date = new Date(_date),
    mnth = ('0' + (date.getMonth() + 1)).slice(-2),
    day = ('0' + date.getDate()).slice(-2);
  return [date.getFullYear(), mnth, day].join('-');
};

/**
 * General way to get the time difference between two dates.
 *
 * To calculate someone's age, set start date to date of birth
 * and end date to today's date.
 *
 * @param start The start date.
 * @param end The end date.
 * @returns Duration
 */
export const calculateDurationDateFns = (
  start: string,
  end: string
): Duration => {
  if (!start || !end) {
    return null;
  }

  // Number fn controls for unix timestamp
  const startDate = Number(start) ? new Date(Number(start)) : new Date(start);
  const endDate = Number(end) ? new Date(Number(end)) : new Date(end);

  return intervalToDuration({ start: startDate, end: endDate });
};

/**
 * Convert a datetime such as 2025-03-27T23:03:38.790Z to Mar 27th, 2025
 */
export function formatDateWithComma(date: Date) {
  if (!date) {
    return '';
  }

  const dateString = date?.toDateString();
  const parts = dateString?.split(' ');

  if (parts.length < 3) {
    return '';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(date);
}
